import { Button, Field, Input } from "@sarva/ui";
import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "@/shared/api";
import { Brand } from "@/shared/layout/Brand";
import { ProblemNotice } from "@/shared/ProblemNotice";
import { chooseOrganisation, configuredTenantId } from "../api/tenant";
import { findOrganisation, useSignInOrganisation } from "../hooks/useSignInOrganisation";

export const FORGOT_PASSWORD_ROUTE = "/forgot-password";
export const RESET_PASSWORD_ROUTE = "/reset-password";
/** The API's floor (`passwords.MIN_PASSWORD_LENGTH`), restated so the form says it first. */
const MIN_LENGTH = 12;

function Card({ title, lead, children }: { title: string; lead: string; children: ReactNode }) {
  return (
    <main className="flex min-h-screen flex-col justify-center bg-canvas px-6 pb-28 pt-12">
      <div className="mx-auto w-full max-w-[27rem] rounded-xl border border-default bg-default p-9 shadow-card sm:p-11">
        <div className="flex flex-col items-center text-center">
          <Brand className="mb-6 h-8" decorative />
          <h1 className="text-2xl font-bold tracking-tight text-default">{title}</h1>
          <p className="mt-1.5 text-sm text-secondary">{lead}</p>
        </div>
        <div className="mt-8 flex flex-col gap-5">{children}</div>
      </div>
    </main>
  );
}

/**
 * Forgot password (the backend's D173). One answer whatever the address: the server never says
 * whether an account exists, and neither does this screen.
 */
export function ForgotPasswordPage() {
  // Backend D202: the organisation the sign-in screen would use, or a code typed here.
  const organisation = useSignInOrganisation();
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const request = useMutation({
    mutationFn: async () => {
      const tenantId =
        organisation.tenantId !== "" ? organisation.tenantId : (await organisation.choose(code)).tenantId;
      return api.requestPasswordReset({ body: { tenant_id: tenantId, email: email.trim() } });
    },
    retry: 0,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (email.trim() !== "" && (organisation.tenantId !== "" || code.trim() !== ""))
      request.mutate();
  };

  if (request.isSuccess) {
    return (
      <Card title="Check your email" lead="If that address has an account, a link is on its way.">
        <p className="text-sm text-secondary" role="status">
          The link works once and expires in 30 minutes. If nothing arrives in a few minutes,
          check spam, or ask your practice administrator.
        </p>
        <Link className="text-sm font-medium text-primary underline" to="/sign-in">
          Back to sign in
        </Link>
      </Card>
    );
  }
  return (
    <Card title="Forgot your password?" lead="Enter your email and we will send you a link to choose a new one.">
      <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
        {organisation.tenantId === "" ? (
          <Field label="Organisation code" required>
            {(props) => (
              <Input
                {...props}
                identifier
                autoComplete="organization"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            )}
          </Field>
        ) : null}
        <Field label="Email" required>
          {(props) => (
            <Input
              {...props}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        {request.isError ? <ProblemNotice error={request.error} variant="inline" /> : null}
        <Button type="submit" variant="primary" disabled={request.isPending || email.trim() === ""}>
          Send reset link
        </Button>
        <Link className="text-center text-sm text-secondary underline" to="/sign-in">
          Back to sign in
        </Link>
      </form>
    </Card>
  );
}

/** `#tenant=…&token=…&invite=1` — read from the fragment, which never reaches a server. */
function readFragment(): { tenant: string; token: string; invite: boolean } {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  return {
    tenant: params.get("tenant") ?? configuredTenantId(),
    token: params.get("token") ?? "",
    invite: params.get("invite") === "1",
  };
}

/**
 * Choose a new password from an emailed link — a reset, or the first password from an
 * invitation. The link works once; the server answers every unusable link the same way.
 */
export function ResetPasswordPage() {
  const [{ tenant, token, invite }] = useState(readFragment);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const reset = useMutation({
    mutationFn: () =>
      api.resetPassword({
        body: { tenant_id: tenant, token, new_password: password },
      }),
    retry: 0,
    // Backend D202: the link named the organisation; remember it, so the sign-in that follows
    // goes there and not to the build's default. Best effort — a failed lookup changes nothing.
    onSuccess: () => {
      if (tenant === "") return;
      findOrganisation(tenant).then(chooseOrganisation, () => undefined);
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (password.length < MIN_LENGTH) {
      setLocalError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setLocalError("The two passwords do not match.");
      return;
    }
    setLocalError(null);
    reset.mutate();
  };

  if (token === "" || tenant === "") {
    return (
      <Card title="This link is incomplete" lead="Open the link from your email again, or ask for a new one.">
        <Link className="text-sm font-medium text-primary underline" to={FORGOT_PASSWORD_ROUTE}>
          Ask for a new link
        </Link>
      </Card>
    );
  }
  if (reset.isSuccess) {
    return (
      <Card
        title={invite ? "You're all set" : "Password changed"}
        lead={
          invite
            ? "Your account is ready. Sign in with your email and the password you just chose."
            : "Every other session was signed out. Sign in with your new password."
        }
      >
        <p role="status">
          <Link className="text-sm font-medium text-primary underline" to="/sign-in">
            Go to sign in
          </Link>
        </p>
      </Card>
    );
  }
  return (
    <Card
      title={invite ? "Set your password" : "Choose a new password"}
      lead={`At least ${MIN_LENGTH} characters. A passphrase of a few words is easiest to remember.`}
    >
      <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
        <Field label="New password" required>
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        <Field label="Confirm new password" required {...(localError === null ? {} : { error: localError })}>
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          )}
        </Field>
        {reset.isError ? <ProblemNotice error={reset.error} variant="inline" /> : null}
        {/* H17: the save takes a moment (the password is hashed); say it is working. */}
        <Button
          type="submit"
          variant="primary"
          disabled={reset.isPending}
          loading={reset.isPending}
          loadingLabel={invite ? "Setting your password" : "Changing your password"}
        >
          {invite ? "Set password" : "Change password"}
        </Button>
        {reset.isError ? (
          <Link className="text-center text-sm text-secondary underline" to={FORGOT_PASSWORD_ROUTE}>
            Ask for a new link
          </Link>
        ) : null}
      </form>
    </Card>
  );
}
